// Маппинг ошибок транспорта (ErrorEnvelope сервера → TransportError) в человеческие русские тексты
// (NFR-09: все видимые строки — из strings.ts). Ключуемся на `code` (канон §4.3) и, внутри
// VALIDATION, на фиксированные message-тексты фабрик ошибок identity/directory (§4.9 — тексты
// фабрик зафиксированы контрактом ошибок). Числовые границы берём из `details` конверта
// (minLength/maxLength), НЕ дублируя серверные константы; фолбэк — только если details не пришли.

import { strings } from './strings.js'

interface ApiErrorLike {
  code?: unknown
  message?: unknown
  details?: Record<string, unknown>
}

function asApiError(err: unknown): { code: string; message: string; details: Record<string, unknown> } {
  const e = (typeof err === 'object' && err !== null ? err : {}) as ApiErrorLike
  return {
    code: typeof e.code === 'string' ? e.code : '',
    message: typeof e.message === 'string' ? e.message : '',
    details: typeof e.details === 'object' && e.details !== null ? e.details : {},
  }
}

function numDetail(details: Record<string, unknown>, key: string, fallback: number): number {
  const v = details[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

/** Ошибка POST /auth/register → текст под формой регистрации (вместо «Произошла ошибка»). */
export function registerErrorText(err: unknown): string {
  const e = asApiError(err)
  switch (e.code) {
    case 'VALIDATION':
      if (e.message.includes('password too short')) {
        return strings.auth.passwordTooShort(numDetail(e.details, 'minLength', 8))
      }
      if (e.message.includes('invalid email')) return strings.auth.badEmail
      return strings.errors.checkFields
    case 'FORBIDDEN':
      return strings.auth.corpOnly
    case 'CONFLICT':
      return strings.auth.emailTaken
    case 'RATE_LIMITED':
      return strings.errors.tooManyAttempts
    default:
      return strings.errors.generic
  }
}

/** PWD-4: ошибка POST /auth/change-password на экране обязательной смены → текст под формой. */
export function forcePasswordErrorText(err: unknown): string {
  const e = asApiError(err)
  switch (e.code) {
    case 'VALIDATION':
      if (e.message.includes('current password is incorrect')) return strings.forcePassword.wrongCurrent
      if (e.message.includes('must differ')) return strings.forcePassword.sameAsTemp
      if (e.message.includes('password too short')) {
        return strings.forcePassword.tooShort(numDetail(e.details, 'minLength', 8))
      }
      return strings.errors.checkFields
    case 'RATE_LIMITED':
      return strings.errors.tooManyAttempts
    default:
      return strings.errors.generic
  }
}

/** Ошибка PATCH /users/me → текст тоста настроек (вместо «Не удалось выполнить действие»). */
export function profileErrorText(err: unknown): string {
  const e = asApiError(err)
  if (e.code === 'PAYLOAD_TOO_LARGE') return strings.errors.uploadTooLarge
  if (e.code === 'VALIDATION') {
    if (e.message.includes('unsupported avatar type')) return strings.errors.unsupportedImage
    if (e.message.includes('avatar upload timed out')) return strings.errors.uploadFailed
    if (e.message.includes('name must not be empty')) return strings.settings.nameEmpty
    if (e.message.includes('name too long')) {
      return strings.settings.nameTooLong(numDetail(e.details, 'maxLength', 80))
    }
    if (e.message.includes('title too long')) {
      return strings.settings.titleTooLong(numDetail(e.details, 'maxLength', 80))
    }
    if (e.message.includes('department too long')) {
      return strings.settings.departmentTooLong(numDetail(e.details, 'maxLength', 80))
    }
    return strings.errors.checkFields
  }
  if (e.code === 'RATE_LIMITED') return strings.errors.tooManyAttempts
  return strings.errors.actionFailed
}
